import { useMemo } from "react";
import type { ColumnsType } from "antd/es/table";
import Datatable from "../../../core/common/dataTable";
import ExportExcel from "../../../core/common/exportExcel";
import useApi from "../../../API/useApi";

interface TrainingFeedbackRecord {
    key: string;
    timestamp: string;
    emailAddress: string;
    trainingStartDate: string;
    trainingEndDate: string;
    classesGradesTaught: string;
    learningObjectives: string;
    concernsQuestions: string;
    instructorEffectiveness: string;
    trainingEngagement: string;
    domainUnderstanding: string;
    handsOnActivities: string;
    postSessionKnowledge: string;
    perspectiveOnImpact: string;
    doubtsQueriesAddressed: string;
    confidenceExplainingConcepts: string;
    overallTOPExperience: string;
    problemSolvingSkills: string;
    handsOnProjectExperience: string;
    labVisitExperience: string;
    recommendationLikelihood: string;
    furtherSuggestions: string;
}

// API's question_code -> our column's dataIndex.
// (Add entries here if the backend introduces new question codes.)
const QUESTION_CODE_TO_FIELD: Record<string, keyof TrainingFeedbackRecord> = {
    learningObjectives: "learningObjectives",
    concernsAddressed: "concernsQuestions",
    instructorEffectiveness: "instructorEffectiveness",
    engagingInteractive: "trainingEngagement",
    understandingEnhanced: "domainUnderstanding",
    handsOnEngaging: "handsOnActivities",
    overallKnowledge: "postSessionKnowledge",
    perspectiveChanged: "perspectiveOnImpact",
    doubtsAddressed: "doubtsQueriesAddressed",
    confidenceExplaining: "confidenceExplainingConcepts",
    topOverallExperience: "overallTOPExperience",
    topProblemSolving: "problemSolvingSkills",
    topHandsOn: "handsOnProjectExperience",
    labVisit: "labVisitExperience",
    recommendLikelihood: "recommendationLikelihood",
    furtherSuggestions: "furtherSuggestions",
};

interface ApiAnswer {
    answer: number | string;
    question: string;
    question_code: string;
}

interface ApiSubmission {
    student_id: string;
    student_name: string;
    submission_id: number;
    submitted_at: string;
    email?: string;
    classes_taught?: string;
    arrival_date?: string;
    departure_date?: string;
    suggestions?: string;
    answers: ApiAnswer[];
}

function formatTimestamp(iso: string): string {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(
        d.getHours()
    )}:${pad(d.getMinutes())}`;
}

function mapSubmissionToRecord(submission: ApiSubmission): TrainingFeedbackRecord {
    // Base record — fields the answers endpoint doesn't provide are left blank.
    const record: TrainingFeedbackRecord = {
        key: String(submission.submission_id),
        timestamp: formatTimestamp(submission.submitted_at),
        emailAddress: submission.email ?? "",
        trainingStartDate: formatTimestamp(submission.arrival_date ?? ""),
        trainingEndDate: formatTimestamp(submission.departure_date ?? ""),
        classesGradesTaught: submission.classes_taught ?? "",
        learningObjectives: "",
        concernsQuestions: "",
        instructorEffectiveness: "",
        trainingEngagement: "",
        domainUnderstanding: "",
        handsOnActivities: "",
        postSessionKnowledge: "",
        perspectiveOnImpact: "",
        doubtsQueriesAddressed: "",
        confidenceExplainingConcepts: "",
        overallTOPExperience: "",
        problemSolvingSkills: "",
        handsOnProjectExperience: "",
        labVisitExperience: "",
        recommendationLikelihood: "",
        furtherSuggestions: submission.suggestions ?? "",
    };

    submission.answers.forEach((a) => {
        const field = QUESTION_CODE_TO_FIELD[a.question_code];
        if (field) {
            (record as any)[field] = String(a.answer);
        }
    });

    return record;
}

/* ------------------------------------------------------------------ */
/* Charts                                                              */
/* ------------------------------------------------------------------ */

// Palette taken from the app's green theme (sidebar active item / Export button).
const THEME_GREEN = "#16a34a";
const PIE_COLORS = [
    "#16a34a",
    "#4ade80",
    "#15803d",
    "#86efac",
    "#14532d",
    "#bbf7d0",
    "#22c55e",
    "#065f46",
];
const AXIS_COLOR = "#6b7280";
const GRID_COLOR = "#e5e7eb";
const TITLE_COLOR = "#1b2850"; // same navy as the page heading

interface Slice {
    label: string;
    count: number;
}

/** Count how often each (non-empty) value appears in a column. */
function countBy(
    records: TrainingFeedbackRecord[],
    field: keyof TrainingFeedbackRecord
): Slice[] {
    const map = new Map<string, number>();
    records.forEach((r) => {
        const v = String(r[field] ?? "").trim();
        if (!v) return;
        map.set(v, (map.get(v) ?? 0) + 1);
    });
    return Array.from(map.entries())
        .map(([label, count]) => ({ label, count }))
        .sort((a, b) => b.count - a.count);
}

/**
 * Distribution for rating-type answers. If every answer is a number 1-5 the
 * result is always 1..5 (zeros included, like the Google Forms charts);
 * otherwise the distinct text answers are used.
 */
function ratingDistribution(
    records: TrainingFeedbackRecord[],
    field: keyof TrainingFeedbackRecord
): Slice[] {
    const values = records
        .map((r) => String(r[field] ?? "").trim())
        .filter(Boolean);
    const allNumeric =
        values.length > 0 && values.every((v) => /^[1-5]$/.test(v));
    if (allNumeric) {
        return [1, 2, 3, 4, 5].map((n) => ({
            label: String(n),
            count: values.filter((v) => v === String(n)).length,
        }));
    }
    return countBy(records, field);
}

function ChartCard({
    title,
    total,
    children,
}: {
    title: string;
    total: number;
    children: React.ReactNode;
}) {
    return (
        <div className="card shadow-sm border-0 rounded-4 h-100">
            <div className="card-body">
                <h6
                    className="mb-1"
                    style={{ color: TITLE_COLOR, fontWeight: 600, lineHeight: 1.35 }}
                >
                    {title}
                </h6>
                <div className="text-muted mb-3" style={{ fontSize: 12 }}>
                    {total} responses
                </div>
                {total === 0 ? (
                    <div
                        className="text-muted d-flex align-items-center justify-content-center"
                        style={{ height: 200, fontSize: 13 }}
                    >
                        No data available
                    </div>
                ) : (
                    children
                )}
            </div>
        </div>
    );
}

function BarChart({ data }: { data: Slice[] }) {
    const total = data.reduce((s, d) => s + d.count, 0);
    const rawMax = Math.max(...data.map((d) => d.count), 1);
    // round the axis top up to a multiple of 4 so we get 4 even gridlines
    const max = Math.ceil(rawMax / 4) * 4 || 4;

    const W = 420;
    const H = 230;
    const padL = 32;
    const padR = 8;
    const padT = 16;
    const padB = 44;
    const innerW = W - padL - padR;
    const innerH = H - padT - padB;
    const slot = innerW / data.length;
    const barW = Math.min(slot * 0.6, 56);
    const ticks = [0, 1, 2, 3, 4].map((i) => (max / 4) * i);

    const short = (s: string) => (s.length > 14 ? s.slice(0, 13) + "…" : s);

    return (
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img">
            {ticks.map((t) => {
                const y = padT + innerH - (t / max) * innerH;
                return (
                    <g key={t}>
                        <line x1={padL} x2={W - padR} y1={y} y2={y} stroke={GRID_COLOR} />
                        <text
                            x={padL - 6}
                            y={y + 4}
                            textAnchor="end"
                            fontSize="10"
                            fill={AXIS_COLOR}
                        >
                            {Number.isInteger(t) ? t : t.toFixed(1)}
                        </text>
                    </g>
                );
            })}

            {data.map((d, i) => {
                const h = (d.count / max) * innerH;
                const x = padL + slot * i + (slot - barW) / 2;
                const y = padT + innerH - h;
                const pct = total ? Math.round((d.count / total) * 100) : 0;
                return (
                    <g key={d.label}>
                        {d.count > 0 && (
                            <rect
                                x={x}
                                y={y}
                                width={barW}
                                height={h}
                                rx={3}
                                fill={THEME_GREEN}
                            >
                                <title>{`${d.label}: ${d.count} (${pct}%)`}</title>
                            </rect>
                        )}
                        <text
                            x={x + barW / 2}
                            y={d.count > 0 ? y - 5 : padT + innerH - 5}
                            textAnchor="middle"
                            fontSize="10"
                            fontWeight={600}
                            fill={TITLE_COLOR}
                        >
                            {d.count} ({pct}%)
                        </text>
                        <text
                            x={x + barW / 2}
                            y={H - padB + 16}
                            textAnchor="middle"
                            fontSize="11"
                            fill={AXIS_COLOR}
                        >
                            {short(d.label)}
                        </text>
                    </g>
                );
            })}
            <line
                x1={padL}
                x2={W - padR}
                y1={padT + innerH}
                y2={padT + innerH}
                stroke="#9ca3af"
            />
        </svg>
    );
}

function PieChart({ data }: { data: Slice[] }) {
    const total = data.reduce((s, d) => s + d.count, 0);
    const R = 80;
    const C = 90;

    let angle = -Math.PI / 2;
    const slices = data.map((d, i) => {
        const frac = total ? d.count / total : 0;
        const start = angle;
        const end = angle + frac * Math.PI * 2;
        angle = end;
        const large = end - start > Math.PI ? 1 : 0;
        const x1 = C + R * Math.cos(start);
        const y1 = C + R * Math.sin(start);
        const x2 = C + R * Math.cos(end);
        const y2 = C + R * Math.sin(end);
        const mid = (start + end) / 2;
        return {
            ...d,
            frac,
            color: PIE_COLORS[i % PIE_COLORS.length],
            path:
                frac >= 0.9999
                    ? null
                    : `M${C},${C} L${x1},${y1} A${R},${R} 0 ${large} 1 ${x2},${y2} Z`,
            lx: C + R * 0.62 * Math.cos(mid),
            ly: C + R * 0.62 * Math.sin(mid),
        };
    });

    return (
        <div className="d-flex align-items-center flex-wrap gap-3">
            <svg viewBox="0 0 180 180" width={190} height={190} role="img">
                {slices.map((s) =>
                    s.path ? (
                        <path key={s.label} d={s.path} fill={s.color} stroke="#fff" strokeWidth={1.5}>
                            <title>{`${s.label}: ${s.count} (${Math.round(s.frac * 100)}%)`}</title>
                        </path>
                    ) : (
                        <circle key={s.label} cx={C} cy={C} r={R} fill={s.color} />
                    )
                )}
                {slices.map(
                    (s) =>
                        s.frac >= 0.06 && (
                            <text
                                key={`t-${s.label}`}
                                x={s.lx}
                                y={s.ly + 4}
                                textAnchor="middle"
                                fontSize="11"
                                fontWeight={600}
                                fill={
                                    ["#bbf7d0", "#86efac", "#4ade80"].includes(s.color)
                                        ? "#14532d"
                                        : "#fff"
                                }
                            >
                                {Math.round(s.frac * 100)}%
                            </text>
                        )
                )}
            </svg>

            <ul className="list-unstyled mb-0" style={{ fontSize: 13, minWidth: 140 }}>
                {slices.map((s) => (
                    <li key={s.label} className="d-flex align-items-center gap-2 mb-1">
                        <span
                            style={{
                                width: 10,
                                height: 10,
                                borderRadius: 2,
                                background: s.color,
                                display: "inline-block",
                                flexShrink: 0,
                            }}
                        />
                        <span style={{ color: "#374151" }}>
                            {s.label} <span className="text-muted">({s.count})</span>
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

// Which columns get a chart, and what kind.
const PIE_CHARTS: { field: keyof TrainingFeedbackRecord; title: string }[] = [
    { field: "classesGradesTaught", title: "Which classes/grades did you teach?" },
];

const BAR_CHARTS: { field: keyof TrainingFeedbackRecord; title: string }[] = [
    {
        field: "learningObjectives",
        title: "To what extent did the training program meet your learning objectives?",
    },
    {
        field: "concernsQuestions",
        title: "How effectively did the training program address your concerns and questions?",
    },
    {
        field: "instructorEffectiveness",
        title: "How would you rate the instructor's effectiveness in delivering the training?",
    },
    {
        field: "trainingEngagement",
        title: "How engaging and interactive was the training program?",
    },
    {
        field: "domainUnderstanding",
        title: "How much did the training enhance your understanding of Emerging Technology/Robotics & IOT/ AI & Coding in your domain?",
    },
    {
        field: "handsOnActivities",
        title: "How engaging were the hands-on activities and projects?",
    },
    {
        field: "postSessionKnowledge",
        title: "Overall knowledge of Emerging Technology/Robotics & IOT/ AI & Coding, post sessions",
    },
    {
        field: "perspectiveOnImpact",
        title: "How has the training changed your perspective on the impact of these technologies on society?",
    },
    {
        field: "doubtsQueriesAddressed",
        title: "Were your doubts and queries addressed?",
    },
    {
        field: "confidenceExplainingConcepts",
        title: "Confidence in explaining concepts",
    },
    {
        field: "overallTOPExperience",
        title: "Overall experience with the TOP sessions",
    },
    {
        field: "problemSolvingSkills",
        title: "How effectively did the TOP sessions enhance your problem-solving skills?",
    },
    {
        field: "handsOnProjectExperience",
        title: "Did the TOP sessions provide enough hands-on experience for project development?",
    },
    { field: "labVisitExperience", title: "How was your experience in the lab visit?" },
    {
        field: "recommendationLikelihood",
        title: "How likely are you to recommend this training program to others?",
    },
];

const columns: ColumnsType<TrainingFeedbackRecord> = [
    {
        title: "Timestamp",
        dataIndex: "timestamp",
        key: "timestamp",
        width: 160,
    },
    {
        title: "Email Address",
        dataIndex: "emailAddress",
        key: "emailAddress",
        width: 200,
    },
    // {
    //     title: "Training Start Date",
    //     dataIndex: "trainingStartDate",
    //     key: "trainingStartDate",
    //     width: 160,
    // },
    // {
    //     title: "Training End Date",
    //     dataIndex: "trainingEndDate",
    //     key: "trainingEndDate",
    //     width: 160,
    // },
    {
        title: "Classes / Grades Taught",
        dataIndex: "classesGradesTaught",
        key: "classesGradesTaught",
        width: 180,
    },
    {
        title: "To what extent did the training program meet your learning objectives?",
        dataIndex: "learningObjectives",
        key: "learningObjectives",
        width: 220,
        ellipsis: true,
    },
    {
        title: "How effectively did the training program address your concerns and questions?",
        dataIndex: "concernsQuestions",
        key: "concernsQuestions",
        width: 220,
        ellipsis: true,
    },
    {
        title: "How would you rate the instructor's effectiveness in delivering the training?",
        dataIndex: "instructorEffectiveness",
        key: "instructorEffectiveness",
        width: 220,
        ellipsis: true,
    },
    {
        title: "How engaging and interactive was the training program?",
        dataIndex: "trainingEngagement",
        key: "trainingEngagement",
        width: 220,
        ellipsis: true,
    },
    {
        title: "To what extent did the training program enhance your understanding of Emerging Technology/Robotics & IOT/ AI & Coding concept related to your domain? ",
        dataIndex: "domainUnderstanding",
        key: "domainUnderstanding",
        width: 220,
        ellipsis: true,
    },
    {
        title: "How engaging were the hands-on activities and projects? ",
        dataIndex: "handsOnActivities",
        key: "handsOnActivities",
        width: 220,
        ellipsis: true,
    },
    {
        title: "How would you rate your overall knowledge of Emerging Technology/Robotics & IOT/ AI & Coding related to your domain post sessions ?",
        dataIndex: "postSessionKnowledge",
        key: "postSessionKnowledge",
        width: 220,
        ellipsis: true,
    },
    {
        title: "How has this training program changed your perspective on the potential impact of Emerging Technology/Robotics & IOT/ AI & Coding on society ?",
        dataIndex: "perspectiveOnImpact",
        key: "perspectiveOnImpact",
        width: 220,
        ellipsis: true,
    },
    {
        title: "Were your doubts and queries addressed ?",
        dataIndex: "doubtsQueriesAddressed",
        key: "doubtsQueriesAddressed",
        width: 220,
        ellipsis: true,
    },
    {
        title: "Confidence in Explaining Concepts",
        dataIndex: "confidenceExplainingConcepts",
        key: "confidenceExplainingConcepts",
        width: 200,
        ellipsis: true,
    },
    {
        title: "How would you rate your overall experience with the TOP sessions ?",
        dataIndex: "overallTOPExperience",
        key: "overallTOPExperience",
        width: 220,
        ellipsis: true,
    },
    {
        title: "How effectively did the TOP sessions enhance your problem-solving skills ?",
        dataIndex: "problemSolvingSkills",
        key: "problemSolvingSkills",
        width: 220,
        ellipsis: true,
    },
    {
        title: "Did the TOP sessions provide enough hands-on experience for project development?",
        dataIndex: "handsOnProjectExperience",
        key: "handsOnProjectExperience",
        width: 220,
        ellipsis: true,
    },
    {
        title: "How was your experience in lab visit",
        dataIndex: "labVisitExperience",
        key: "labVisitExperience",
        width: 200,
        ellipsis: true,
    },
    {
        title: "How likely are you to recommend this training program to others?",
        dataIndex: "recommendationLikelihood",
        key: "recommendationLikelihood",
        width: 220,
        ellipsis: true,
    },
    {
        title: "Any further suggestions you would like to convey",
        dataIndex: "furtherSuggestions",
        key: "furtherSuggestions",
        width: 220,
        ellipsis: true,
    },
];

export default function Posttraining() {

    const { data, loading } = useApi("/get-postfeedback-reports", {
        method: "GET",
        autoCall: true
    })

    const tableData = useMemo<TrainingFeedbackRecord[]>(() => {
        const submissions: ApiSubmission[] = data?.data ?? [];
        return submissions.map(mapSubmissionToRecord);
    }, [data]);

    const pieData = useMemo(
        () => PIE_CHARTS.map((c) => ({ ...c, slices: countBy(tableData, c.field) })),
        [tableData]
    );

    const barData = useMemo(
        () =>
            BAR_CHARTS.map((c) => ({
                ...c,
                slices: ratingDistribution(tableData, c.field),
            })),
        [tableData]
    );

    const sum = (s: Slice[]) => s.reduce((a, b) => a + b.count, 0);

    return (
        <div className="page-wrapper">
            <div className="content">

                {/* ---------- Page title ---------- */}
                <div
                    className="card shadow-sm border-0 rounded-4 mb-3"
                    style={{ borderLeft: `5px solid ${THEME_GREEN}`, overflow: "hidden" }}
                >
                    <div className="card-body d-flex align-items-center justify-content-between flex-wrap gap-3">
                        <div className="d-flex align-items-center gap-3">
                            <div
                                className="d-flex align-items-center justify-content-center"
                                style={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: 14,
                                    background: "#dcfce7",
                                    color: THEME_GREEN,
                                    flexShrink: 0,
                                }}
                            >
                                <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <line x1="18" y1="20" x2="18" y2="10" />
                                    <line x1="12" y1="20" x2="12" y2="4" />
                                    <line x1="6" y1="20" x2="6" y2="14" />
                                </svg>
                            </div>
                            <div>
                                <h1
                                    className="mb-0"
                                    style={{
                                        fontSize: "1.6rem",
                                        fontWeight: 700,
                                        color: TITLE_COLOR,
                                        letterSpacing: "-0.01em",
                                    }}
                                >
                                    Post-training feedback
                                </h1>
                                <div className="text-muted" style={{ fontSize: 13 }}>
                                    Participant ratings and responses collected after the training
                                </div>
                            </div>
                        </div>

                        <span
                            style={{
                                background: "#dcfce7",
                                color: "#15803d",
                                fontWeight: 600,
                                fontSize: 13,
                                padding: "6px 14px",
                                borderRadius: 999,
                            }}
                        >
                            {loading ? "Loading…" : `${tableData.length} responses`}
                        </span>
                    </div>
                </div>

                {/* ---------- Charts ---------- */}
                {!loading && (
                    <div className="row g-3 mb-3 stagger-children">
                        {pieData.map((c) => (
                            <div className="col-12 col-lg-6" key={c.field}>
                                <ChartCard title={c.title} total={sum(c.slices)}>
                                    <PieChart data={c.slices} />
                                </ChartCard>
                            </div>
                        ))}
                        {barData.map((c) => (
                            <div className="col-12 col-lg-6" key={c.field}>
                                <ChartCard title={c.title} total={sum(c.slices)}>
                                    <BarChart data={c.slices} />
                                </ChartCard>
                            </div>
                        ))}
                    </div>
                )}

                {/* ---------- Table (export lives in the table header) ---------- */}
                <div className="card shadow-sm border-0 rounded-4">
                    <div className="card-header bg-transparent d-flex align-items-center justify-content-between flex-wrap gap-2 pt-3 pb-3">
                        <h5 className="mb-0" style={{ color: TITLE_COLOR, fontWeight: 600 }}>
                            All responses
                        </h5>

                        <ExportExcel
                            columns={columns}
                            dataSource={tableData}
                            fileName="post_training_feedback"
                            buttonLabel="Export to Excel"
                        />
                    </div>
                    <div className="card-body p-0">
                        <Datatable
                            columns={columns}
                            dataSource={tableData}
                            loading={loading}
                        // rowKey="timestamp"
                        // scroll={{ x: 2500 }}
                        />
                    </div>
                </div>

            </div>
        </div>
    )
}